import React, { useCallback, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import {
  SavedRouteAlert,
  SavedRouteAlertRoute,
  useUpdateSavedRouteAlertMutation,
} from 'state/emailAlertsSlices';
import { SavedRoute } from 'state/savedRouteSlices';

import StlSwitch from 'features/common/StlSwitch/StlSwitch';

import { AlertListItem } from './AlertListItem';
import { formatAlertName } from './alertHelpers';
import { useNotificationProject } from './useNotificationProject';

import styles from './AllAlertsList.module.css';

interface AllAlertsListProps {
  alerts: SavedRouteAlert[];
  route: SavedRoute;
}

export function AllAlertsList({ alerts, route }: AllAlertsListProps) {
  const [selectedAlert, setSelectedAlert] = useState<SavedRouteAlert | null>(
    null,
  );
  const { projectSlug } = useNotificationProject();
  const [updateAlert] = useUpdateSavedRouteAlertMutation();

  const handleToggleRoute = (alert: SavedRouteAlert, shouldAssign: boolean) => {
    if (!projectSlug) return;

    const currentRoutes = alert.routes;
    let updatedRoutes: SavedRouteAlertRoute[];

    if (shouldAssign) {
      // add route
      updatedRoutes = [
        ...currentRoutes,
        { id: route.id, name: route.routeName },
      ];
    } else {
      // remove route
      updatedRoutes = currentRoutes.filter(
        (currentRoute) => currentRoute.id !== route.id,
      );
    }
    updateAlert({
      project_slug: projectSlug,
      alert_id: alert.hash_id,
      data: {
        routes: updatedRoutes,
      },
    });
  };

  const handleAlertClick = useCallback(
    (alertId: string) => {
      const alert = alerts.find((a) => a.hash_id === alertId);
      if (alert) {
        setSelectedAlert(alert);
      }
    },
    [alerts],
  );

  return (
    <Stack direction="row" spacing={0.25} className={styles.container}>
      <Stack spacing={1} className={styles.alertsList}>
        <Stack spacing={0.25}>
          {alerts.map((alert) => {
            const isAssigned = alert.routes.some(
              (alertRoute) => alertRoute.id === route.id,
            );
            return (
              <Box key={alert.hash_id} className={styles.alertContainer}>
                <AlertListItem
                  alert={alert}
                  onClick={handleAlertClick}
                  previewMode
                />
                <Box className={styles.switchContainer}>
                  <StlSwitch
                    checked={isAssigned}
                    onChange={(e) => handleToggleRoute(alert, e.target.checked)}
                  />
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Stack>
      <Stack className={styles.recipientsPanel}>
        {selectedAlert && (
          <Stack direction="row" alignItems="center" spacing="0.5rem">
            <Typography variant="h4" className={styles.recipientsTitle}>
              Recipients
            </Typography>
            <Box className={styles.chipTitle}>
              <Typography variant="caption">
                {formatAlertName(
                  selectedAlert.metric_type,
                  selectedAlert.metric_criteria,
                )}
              </Typography>
            </Box>
          </Stack>
        )}
        <Stack>
          {selectedAlert?.recipients.map((recipient, index) => (
            <Typography
              key={index}
              variant="caption"
              className={styles.recipientItem}
            >
              {recipient}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
}
